/* eslint-disable import/no-cycle */
import Sockette from 'sockette';
import getAipUrls from '../../config/getAipUrls';
import { selectWsMessage } from '../../commons/util/aip-tools/messageUtil.ws';
import { getAccessToken } from '../../scanline/utils/token';
import { isTokenExpired } from '../../util/user';
import AccountUtil from '../../account/components/utils/AccountUtil';

const DEFAULT_DOWNLOAD_ACTION = 'export-xls';

const isConnectionOpenedMessage = e => {
	const { data } = e;
	return data === 'Message Received';
};

const ignoreMessage = e => isConnectionOpenedMessage(e);

const checkAssetToken = () => {
	if (isTokenExpired()) {
		return AccountUtil.refresh();
	}
	return Promise.resolve();
};

const normalizeHandlers = handlers => {
	if (typeof handlers === 'function') {
		return {
			handleResponse: handlers,
			onMessage: () => {}
		};
	}

	return handlers || {};
};

const getWsErrorMessage = wsMessage => {
	if (!wsMessage || typeof wsMessage !== 'object') {
		return '';
	}

	return wsMessage.error || wsMessage.err || '';
};

class ExportWebSocket {
	constructor() {
		this.ws = undefined;
	}

	startDownload = (payload, handlers = {}, action = DEFAULT_DOWNLOAD_ACTION) => {
		const {
			handleResponse = () => {},
			onMessage = () => {}
		} = normalizeHandlers(handlers);
		let settled = false;
		const finish = (err, res) => {
			if (settled) {
				return;
			}
			settled = true;
			this._destroyWebSocket();
			handleResponse(err, res);
		};

		if (this.ws) {
			// window.alert('File download already in progress.');
			handleResponse(new Error('File download already in progress.'));
			return false;
		}
		checkAssetToken().then(() => {
			const accessToken = getAccessToken();

			const websocketURL = `${getAipUrls().assetviewWS}?token=${accessToken}`;
			this.ws = new Sockette(websocketURL, {
				maxAttempts: 5,
				onopen: e => this._send(e, payload, action),
				onmessage: e => this._receiveMessage(e, { finish, onMessage }),
				onreconnect: e => console.log('Reconnecting...', e),
				onmaximum: e => {
					console.log('Stop Attempting!', e);
					finish(new Error('Unable to connect to the export service.'));
				},
				onerror: e => console.log('Error:', e)
			});
		}).catch(err => {
			finish(err || new Error('Unable to refresh the export session.'));
		});

		return true;
	};

	destroy = () => {
		this._destroyWebSocket();
	};

	_send = (_e, params, action) => {
		const payload = {
			action,
			...params
		};

		this.ws.send(JSON.stringify(payload));
		const intervalID = setInterval(() => {
			if (!this.ws) {
				clearInterval(intervalID);
				return;
			}
			this.ws.send('ping');
		}, 20000);
	};

	_receiveMessage = (wsEvent, { finish, onMessage }) => {
		if (ignoreMessage(wsEvent)) {
			return;
		}

		const wsMessage = selectWsMessage(wsEvent);
		const errorMessage = getWsErrorMessage(wsMessage);
		const error = errorMessage ? new Error(errorMessage) : undefined;
		const result = error ? undefined : wsMessage;

		if (error) {
			finish(error);
			return;
		}

		if (result) {
			onMessage(result);
		}

		const { finished, url } = result || {};
		if (url !== undefined) {
			finish(undefined, result);
			return;
		}
		if (finished) {
			finish(new Error('The export completed without returning a download URL.'));
		}
	};

	_destroyWebSocket = () => {
		if (this.ws) {
			this.ws.close();
			this.ws = undefined;
			this.zipsCount = undefined;
		}
	};
}

export default new ExportWebSocket();
